import JSZip from 'jszip';
import mammoth from 'mammoth';

import type { ClauseDocxImportResult } from '../types';

import {
  applyFormattingStyles,
  detectUnstyledHeadings,
  expandSpacingToBlankParagraphs,
  formattingStyleMap,
  inheritStyleBold,
  preserveFormattingTransform,
} from './document-transforms';
import { hasHeaderFooterContent } from './header-footer';
import {
  normaliseFontSizes,
  replaceTabsWithNbsp,
  stripNoteContent,
  stripTextColors,
  stripUnsupportedElements,
  trimEdgeEmptyParagraphs,
} from './html-cleanup';
import { applyListTypes, extractOrderedListFormats, mergeSplitLists, normaliseHtmlLists } from './lists';

// Word built-in body styles that carry no block structure; unmapped they trigger a
// misleading "Unrecognised paragraph style" warning although nothing importable is lost.
const NO_STRUCTURE_BUILTIN_STYLES = [
  'Plain Text',
  'Body Text',
  'Body Text 2',
  'Body Text 3',
  'Body Text Indent',
  'Body Text Indent 2',
  'Body Text Indent 3',
  'No Spacing',
  'Normal (Web)',
  'Normal Indent',
  ...Array.from({ length: 9 }, (_, index) => `toc ${index + 1}`),
];

const FRIENDLY_WARNINGS: [RegExp, string][] = [
  [/^Unrecognised paragraph style/, "A custom paragraph style couldn't be matched, but the text was kept."],
  [/^Unrecognised run style/, "A custom text style couldn't be matched, but the text was kept."],
  [
    /style with ID .* was referenced but not defined/,
    "The document used a style that wasn't defined in the file, but the text was kept.",
  ],
  [/^An unrecognised element was ignored/, 'Part of the document uses an unsupported Word feature and was skipped.'],
  [/^Unsupported break type/, "A layout break couldn't be imported. Your content is unaffected."],
  [/w:sym element with an unsupported character/, "A special symbol couldn't be converted and was removed."],
  [
    /unexpected non-row element in table/,
    'A table had an unusual structure, so its merged cells may not display correctly.',
  ],
  [
    /unexpected non-cell element in table row/,
    'A table row had an unusual structure, so its merged cells may not display correctly.',
  ],
  [/Could not find image file for a:blip/, 'An image was missing from the file and was skipped.'],
  [/v:imagedata element without a relationship ID/, "An older-format image couldn't be read and was skipped."],
  [/Image of type .* is unlikely to display/, "An image was in a format that can't be displayed and was removed."],
  [/Did not understand this style mapping/, 'Something went wrong applying formatting. Your content is unaffected.'],
];

const toFriendlyWarning = (message: string): string | undefined =>
  FRIENDLY_WARNINGS.find(([pattern]) => pattern.test(message))?.[1];

// mammoth ignores underline by default; map it so the editor's underline mark survives import.
const styleMap = [
  'u => u',
  ...NO_STRUCTURE_BUILTIN_STYLES.map((name) => `p[style-name='${name}'] => p:fresh`),
  ...formattingStyleMap,
];

export const convertDocxToClauseHtml = async (file: File): Promise<ClauseDocxImportResult> => {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.convertToHtml(
    { arrayBuffer },
    // empty paragraphs are meaningful in contract wordings (spacing between clauses)
    {
      styleMap,
      transformDocument: (document) =>
        preserveFormattingTransform(inheritStyleBold(detectUnstyledHeadings(expandSpacingToBlankParagraphs(document)))),
      ignoreEmptyParagraphs: false,
    },
  );

  const zip = JSZip.loadAsync(arrayBuffer);
  const [listTypes, droppedHeaderFooter] = await Promise.all([
    extractOrderedListFormats(zip).catch(() => []),
    hasHeaderFooterContent(zip).catch(() => false),
  ]);

  const doc = new DOMParser().parseFromString(result.value, 'text/html');
  stripNoteContent(doc);
  stripTextColors(doc);
  normaliseFontSizes(doc);
  applyFormattingStyles(doc);
  replaceTabsWithNbsp(doc);
  mergeSplitLists(doc);
  normaliseHtmlLists(doc);
  applyListTypes(doc, listTypes);
  const droppedImageCount = stripUnsupportedElements(doc);
  trimEdgeEmptyParagraphs(doc);

  const html = doc.body.innerHTML;
  const warnings: string[] = [];

  for (const { message } of result.messages) {
    const friendly = toFriendlyWarning(message);

    if (friendly && !warnings.includes(friendly)) {
      warnings.push(friendly);
    }
  }

  if (droppedImageCount > 0) {
    warnings.unshift("Images aren't supported and were removed. Your text is unaffected.");
  }

  if (droppedHeaderFooter) {
    warnings.push('Headers and footers were left out. Only the main document was imported.');
  }

  return { html, warnings };
};
